import liveServerStatus from '../../liveServerStatus';

/**
 * Generate HTML email template for monthly score notifications
 * @param {Object} employeeData - Employee information
 * @param {Object} scoreData - Information about the monthly scores
 * @param {string} customMessage - Optional custom message from admin
 * @returns {string} - HTML email content
 */
export const generateMonthlyScoreEmailHTML = (employeeData, scoreData, customMessage = '') => {
  // Add test environment indicator in the header if not in live environment
  const testEnvironmentBanner = !liveServerStatus ? `
    <div style="background-color: #ffeb3b; color: #333; text-align: center; padding: 5px; font-weight: bold; font-size: 12px;">
      ⚠️ TEST EMAIL - No action required ⚠️
    </div>
  ` : '';

  // Get first name for greeting
  const firstName = employeeData.name ? employeeData.name.split(' ')[0] : 'there';

  // Determine score color based on value
  let scoreColor = '#4caf50'; // Green for good score (>14)
  
  if (scoreData.score < 10) {
    scoreColor = '#f44336'; // Red for poor score
  } else if (scoreData.score < 14) {
    scoreColor = '#ff9800'; // Orange for average score
  }

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Monthly Score Report</title>
      <style>
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 0;
          background-color: #f5f5f5;
        }
        .email-container {
          max-width: 600px;
          margin: 0 auto;
          background-color: #ffffff;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
        }
        .email-header {
          background-color: #424242;
          color: white;
          padding: 20px;
          text-align: center;
        }
        .email-header h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
        }
        .email-body {
          padding: 30px;
        }
        .greeting {
          font-size: 18px;
          font-weight: 500;
          margin-bottom: 20px;
          color: #212121;
        }
        .score-box {
          background-color: #f5f5f5;
          border-radius: 6px;
          padding: 25px;
          margin-bottom: 25px;
          border: 1px solid #e0e0e0;
          text-align: center;
        }
        .month-label {
          font-size: 16px;
          font-weight: 500;
          color: #616161;
          margin-bottom: 10px;
        }
        .score-container {
          margin: 20px 0;
        }
        .score-value {
          font-size: 48px;
          font-weight: 700;
          color: ${scoreColor};
          display: inline-block;
        }
        .score-total {
          font-size: 18px;
          color: #757575;
          margin-left: 5px;
        }
        .score-label {
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #757575;
          margin-top: 8px;
        }
        .custom-message {
          background-color: #f9f9f9;
          border-left: 4px solid #9e9e9e;
          padding: 15px;
          margin: 20px 0;
          font-style: italic;
        }
        .divider {
          height: 1px;
          background-color: #e0e0e0;
          margin: 25px 0;
        }
        .cta-button {
          display: inline-block;
          background-color: #4a6da7;
          color: white;
          text-decoration: none;
          padding: 12px 25px;
          border-radius: 4px;
          font-weight: 500;
          margin-top: 5px;
          text-align: center;
        }
        .message {
          margin-bottom: 25px;
          color: #424242;
        }
        .email-footer {
          background-color: #212121;
          padding: 20px;
          text-align: center;
          color: #f5f5f5;
          font-size: 12px;
        }
        .company-name {
          font-weight: 600;
          color: #f5f5f5;
          margin-bottom: 8px;
        }
      </style>
    </head>
    <body>
      ${testEnvironmentBanner}
      <div class="email-container">
        <div class="email-header">
          <h1>Monthly Performance Score</h1>
        </div>
        
        <div class="email-body">
          <div class="greeting">
            Hello ${firstName},
          </div>
          
          <div class="message">
            Your monthly performance evaluation has been completed. Please review your score and feedback below.
          </div>
          
          <div class="score-box">
            <div class="month-label">${scoreData.month} ${scoreData.year}</div>
            <div class="score-container">
              <span class="score-value">${scoreData.score}</span>
              <span class="score-total">/20</span>
            </div>
            <div class="score-label">Performance Score</div>
          </div>
          
          ${customMessage ? `
          <div class="custom-message">
            ${customMessage.replace(/\n/g, '<br>')}
          </div>
          ` : ''}
          
          <a href="${process.env.REACT_APP_URL || 'https://revamped-ppc.myelinz.com'}/performance-review" class="cta-button">View Score Details</a>
          
          <div class="divider"></div>
          
          <div class="message">
            Your line manager will be in touch regarding the next steps. If you have any questions about your score or performance evaluation, please reach out to your manager directly.
          </div>
        </div>
        
        <div class="email-footer">
          <div class="company-name">MyelinZ PPC Portal</div>
          <p>This is an automated message. Please do not reply directly to this email.</p>
          <p>© ${new Date().getFullYear()} MyelinZ. All rights reserved.</p>
          ${!liveServerStatus ? '<p><strong>TEST ENVIRONMENT - No action required</strong></p>' : ''}
        </div>
      </div>
    </body>
    </html>
  `;
};

export default generateMonthlyScoreEmailHTML; 